import { Injectable } from '@angular/core';
import { Logger } from '@app/utils/logger';
import { AvatarCacheEntry, ImageType } from '../types/avatar-types';

/**
 * Avatar Cache Service
 * Handles client-side caching of avatar images using localStorage.
 */
@Injectable({
  providedIn: 'root' // eslint-disable-line
})
export class AvatarCacheService {
  private readonly CACHE_PREFIX = 'avatar_cache_';
  private readonly CACHE_TTL = 1 * 60 * 1000; // 1 minute in milliseconds

  log = Logger.getInstance('AvatarCacheService');

  /**
   * Generate cache key.
   *
   * @param {string} profileId - User profile ID.
   * @param {ImageType} imageType - Type of avatar image.
   * @private
   * @returns {string} Cache key.
   */
  private getCacheKey(profileId: string, imageType: ImageType): string {
    return `${this.CACHE_PREFIX}${profileId}_${imageType}`;
  }

  /**
   * Clear all avatar caches.
   */
  clear(): void {
    try {
      // Find all keys that start with the cache prefix
      const keysToRemove: Array<string> = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      // Remove all found keys
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      this.log.error('[AvatarCacheService] Error clearing cache:', error);
    }
  }

  /**
   * Get cached avatar image URL.
   *
   * @param {string} profileId - User profile ID.
   * @param {ImageType} imageType - Type of avatar image.
   * @returns {string | null} Cached image URL or null if not cached or expired.
   */
  get(profileId: string, imageType: ImageType): string | null {
    try {
      const cacheKey = this.getCacheKey(profileId, imageType);
      const cachedData = localStorage.getItem(cacheKey);

      if (!cachedData) {
        return null;
      }

      const cacheEntry: AvatarCacheEntry = JSON.parse(cachedData);

      // Check if cache is expired
      const now = Date.now();
      const age = now - cacheEntry.cachedAt;

      if (age > this.CACHE_TTL) {
        // Cache expired, remove it
        this.invalidate(profileId, imageType);
        return null;
      }

      return cacheEntry.imageUrl;
    } catch (error) {
      this.log.error('[AvatarCacheService] Error getting cache:', error);
      return null;
    }
  }

  /**
   * Get all cached entries (for debugging/monitoring).
   *
   * @returns {Array<AvatarCacheEntry>} Array of all cached entries.
   */
  getAllEntries(): Array<AvatarCacheEntry> {
    try {
      const entries: Array<AvatarCacheEntry> = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_PREFIX)) {
          const cachedData = localStorage.getItem(key);
          if (cachedData) {
            const entry: AvatarCacheEntry = JSON.parse(cachedData);
            entries.push(entry);
          }
        }
      }

      return entries;
    } catch (error) {
      this.log.error('[AvatarCacheService] Error getting all entries:', error);
      return [];
    }
  }

  /**
   * Get cache statistics (for monitoring).
   *
   * @returns {{totalEntries: number, totalSize: number, oldestEntry: number | null, newestEntry: number | null}} Cache statistics object.
   */
  getStats(): {
    totalEntries: number;
    totalSize: number; // approximate size in bytes
    oldestEntry: number | null; // timestamp
    newestEntry: number | null; // timestamp
  } {
    try {
      const entries = this.getAllEntries();

      if (entries.length === 0) {
        return {
          totalEntries: 0,
          totalSize: 0,
          oldestEntry: null,
          newestEntry: null
        };
      }

      // Calculate approximate size
      let totalSize = 0;
      entries.forEach((entry) => {
        totalSize += JSON.stringify(entry).length;
      });

      // Find oldest and newest entries
      const timestamps = entries.map((e) => e.cachedAt).sort((a, b) => a - b);

      return {
        totalEntries: entries.length,
        totalSize,
        oldestEntry: timestamps[0],
        newestEntry: timestamps[timestamps.length - 1]
      };
    } catch (error) {
      this.log.error('[AvatarCacheService] Error getting stats:', error);
      return {
        totalEntries: 0,
        totalSize: 0,
        oldestEntry: null,
        newestEntry: null
      };
    }
  }

  /**
   * Invalidate cached avatar image(s) for a profile.
   *
   * @param {string} profileId - User profile ID.
   * @param {ImageType} [imageType] - Optional specific image type to invalidate (if not provided, invalidates all for profile).
   */
  invalidate(profileId: string, imageType?: ImageType): void {
    try {
      if (imageType) {
        // Invalidate specific image type
        const cacheKey = this.getCacheKey(profileId, imageType);
        localStorage.removeItem(cacheKey);
      } else {
        // Invalidate all image types for this profile
        const allImageTypes: Array<ImageType> = [
          'HOMEPAGE',
          'PROFILE',
          'SKIN',
          'NOURISH',
          '10X',
          'ACTIVE',
          'PERSONA',
          'PRODIGY',
          '360',
          'ONCO_RISK'
        ];

        for (const type of allImageTypes) {
          const cacheKey = this.getCacheKey(profileId, type);
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (error) {
      this.log.error('[AvatarCacheService] Error invalidating cache:', error);
    }
  }

  /**
   * Set cached avatar image URL.
   *
   * @param {string} profileId - User profile ID.
   * @param {ImageType} imageType - Type of avatar image.
   * @param {string} imageUrl - Image URL to cache.
   */
  set(profileId: string, imageType: ImageType, imageUrl: string): void {
    try {
      const cacheKey = this.getCacheKey(profileId, imageType);
      const cacheEntry: AvatarCacheEntry = {
        imageUrl,
        cachedAt: Date.now(),
        imageType,
        profileId
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (error) {
      this.log.error('[AvatarCacheService] Error setting cache:', error);
      // Don't throw error - caching is optional functionality
    }
  }
}
